import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Resource } from '../../models/resource';
import { ResourceService } from '../../services/resource.service';

@Component({
  selector: 'app-resource-composer',
  templateUrl: './resource-composer.component.html',
  styleUrls: ['./resource-composer.component.scss'],
  standalone: false
})
export class ResourceComposerComponent implements OnInit, OnChanges {
  @Input() composerId!: number;
  @Input() viewMode = true;
  @Input() resourceFolder!: string; // Receive the ResourceFolder field from the parent SongComponent
  resources: Resource[] = [];
  selectedResource: Resource | null = null;
  resourceForm!: FormGroup;
  showResourceForm = false;
  selectedFile: File | null = null;

  constructor(private resourceService: ResourceService, private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initForm();
    if (this.composerId) {
      this.loadResources(); // Ensure resources are loaded on initialization
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['composerId'] && changes['composerId'].currentValue) {
      this.loadResources(); // Reload resources when composerId changes
    }
  }

  private initForm(): void {
    this.resourceForm = this.fb.group({
      name: ['', Validators.required],
      isHyperlink: [false],
      resourceType: ['', Validators.required],
      composerId: [null],
      path: [{ value: '', disabled: true }] // Initialize Path as disabled
    });
  }

  loadResources(): void {
    if (!this.composerId) {
      console.error('Composer ID is not set. Cannot load resources.');
      return;
    }

    this.resourceService.getResourcesByComposer(this.composerId).subscribe({
      next: (resources) => {
        if (Array.isArray(resources) && resources.length > 0) {
          this.resources = resources;
          console.log('Resources loaded for composer:', this.composerId, resources);
        } else {
          console.log('No resources found for composer:', this.composerId);
          this.resources = []; // Ensure the resources array is empty
        }
      },
      error: (error) => {
        if (error.status === 404) {
          // Handle 404 as no resources found
          console.log('No resources found for composer (404):', this.composerId);
          this.resources = []; // Ensure the resources array is empty
        } else {
          // Log other errors
          console.error('Error loading resources for composer:', this.composerId, error);
        }
      }
    });
  }

  addResource(): void {
    this.selectedResource = null;
    this.resourceForm.reset({
      isHyperlink: false
    });
    this.showResourceForm = true;
  }

  editResource(resource: Resource): void {
    this.selectedResource = resource;
    this.resourceForm.patchValue(resource);
    this.showResourceForm = true;
  }

  saveResource(): void {
    if (this.resourceForm.valid) {
      const resourceData: Resource = {
        ...this.resourceForm.value,
        composerId: this.composerId,
        resourceEntity: 'Composer', // Ensure resourceEntity is set
        id: this.selectedResource?.id || undefined
      };

      if (!this.resourceForm.get('isHyperlink')?.value) {
        resourceData.path = `/composer/${this.getComposerResourceFolder()}/${this.selectedFile?.name}`;
      }

      if (this.selectedResource) {
        this.resourceService.updateResource(this.selectedResource.id, resourceData).subscribe({
          next: () => {
            if (!this.resourceForm.get('isHyperlink')?.value && this.selectedFile) {
              const formData = new FormData();
              formData.append('file', this.selectedFile);

              this.resourceService.uploadResource(formData, resourceData.path).subscribe({
                next: () => {
                  this.loadResources();
                  this.closeForm();
                },
                error: (error) => {
                  console.error('Error uploading resource:', error);
                }
              });
            } else {
              this.loadResources();
              this.closeForm();
            }
          },
          error: (error) => {
            console.error('Error updating resource:', error);
          }
        });
      } else {
        this.resourceService.createResource(resourceData).subscribe({
          next: (createdResource) => {
            if (!this.resourceForm.get('isHyperlink')?.value && this.selectedFile) {
              const formData = new FormData();
              formData.append('file', this.selectedFile);

              this.resourceService.uploadResource(formData, resourceData.path).subscribe({
                next: () => {
                  this.loadResources();
                  this.closeForm();
                },
                error: (error) => {
                  console.error('Error uploading resource:', error);
                }
              });
            } else {
              this.loadResources();
              this.closeForm();
            }
          },
          error: (error) => {
            console.error('Error creating resource:', error);
          }
        });
      }
    }
  }

  private getComposerResourceFolder(): string {
    //return 'default-composer-folder'; // Revert to default folder logic
    
    // Use the ResourceFolder field passed from the parent SongComponent
    if (!this.resourceFolder) {
      console.error('ResourceFolder is not available.');
      return 'default-folder'; // Fallback to 'default-folder' if not found
    }

    console.log('Fetched resourceFolder from @Input():', this.resourceFolder);
    return this.resourceFolder;
  }

  deleteResource(id: number): void {
    if (confirm('Are you sure you want to delete this resource?')) {
      this.resourceService.deleteResource(id).subscribe({
        next: () => {
          this.loadResources();
        },
        error: (error) => {
          console.error('Error deleting resource:', error);
        }
      });
    }
  }

  closeForm(): void {
    this.selectedResource = null;
    this.showResourceForm = false;
    this.resourceForm.reset();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      console.log('Selected file:', this.selectedFile);
    }
  }

  onIsHyperlinkChange(): void {
    const isHyperlink = this.resourceForm.get('isHyperlink')?.value;
    if (isHyperlink) {
      this.selectedFile = null; // Clear the selected file if "Is Hyperlink" is true
      this.resourceForm.get('path')?.enable(); // Enable the Path field
      console.log('File selector disabled and Path field enabled because "Is Hyperlink" is true.');
    } else {
      this.resourceForm.get('path')?.disable(); // Disable the Path field
      console.log('Path field disabled because "Is Hyperlink" is false.');
    }
  }

  onResourceClick(event: Event, resource: Resource): void {
    event.preventDefault(); // Prevent default browser behavior
    event.stopPropagation(); // Stop event propagation

    console.log('Resource clicked:', resource); // Log the resource for debugging

    if (!resource.isHyperlink) {
      this.viewResource(resource);
    }
  }

  private viewResource(resource: Resource): void {
    if (!resource.path) {
      console.error('Resource path is empty or invalid:', resource);
      return;
    }
    const fallbackName = resource.path.split('/')?.pop() || resource.name || 'resource';
    this.resourceService.downloadResourceResponse(resource.path).subscribe({
      next: (res) => {
        const blob = res.body as Blob;
        if (!blob || blob.size === 0) {
          console.error('Downloaded file is empty. Check the backend response.');
          return;
        }
        const cd = res.headers.get('Content-Disposition');
        const serverFilename = this.extractFilename(cd) || fallbackName;
        const inferredType = this.inferMimeType(serverFilename) || blob.type || 'application/octet-stream';
        const displayBlob = blob.type && blob.type !== '' ? blob : new Blob([blob], { type: inferredType });
        const viewable = inferredType.startsWith('image/') || inferredType === 'application/pdf' || inferredType.startsWith('text/') || inferredType === 'application/json' || inferredType.startsWith('audio/') || inferredType.startsWith('video/');
        const objectUrl = window.URL.createObjectURL(displayBlob);
        if (viewable) {
          const win = window.open(objectUrl, '_blank');
          if (!win) {
            console.warn('Popup blocked. Please allow popups for this site.');
          }
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
        } else {
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = serverFilename;
          a.click();
          window.URL.revokeObjectURL(objectUrl);
        }
      },
      error: (error) => console.error('Error fetching resource for viewing:', error)
    });
  }

  private inferMimeType(fileName: string): string {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'gif': return 'image/gif';
      case 'webp': return 'image/webp';
      case 'svg': return 'image/svg+xml';
      case 'pdf': return 'application/pdf';
      case 'txt': return 'text/plain';
      case 'html':
      case 'htm': return 'text/html';
      case 'json': return 'application/json';
      case 'mp3': return 'audio/mpeg';
      case 'wav': return 'audio/wav';
      case 'mp4': return 'video/mp4';
      case 'mov': return 'video/quicktime';
      default: return 'application/octet-stream';
    }
  }

  private extractFilename(contentDisposition: string | null): string | null {
    if (!contentDisposition) return null;
    const filenameStarMatch = /filename\*=UTF-8''([^;\n]+)/i.exec(contentDisposition);
    if (filenameStarMatch && filenameStarMatch[1]) {
      try { return decodeURIComponent(filenameStarMatch[1]); } catch { /* ignore */ }
    }
    const filenameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return filenameMatch && filenameMatch[1] ? filenameMatch[1] : null;
  }
}
